import {Injectable} from "@angular/core";
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {DataSetUsagesService} from "../../services/data-set-usages.service";
import * as dataSourcesCustom from "./dataSourcesCustom.actions";
import {exhaustMap, forkJoin, map, Observable, switchMap, withLatestFrom} from 'rxjs';
import {TypedAction} from '@ngrx/store/src/models';
import {
  loadDataSetDimensions,
  loadDataSetDimensionsSuccess,
  updateDataSetDimension, updateDataSetDimensionsSuccess,
  updateDataSetDimensionSuccess
} from '../dataSetDimensions/dataSetDimensions.actions';
import {DimensionComponentObjects} from '../../classes/dimension-component-object';
import {selectDataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions.selectors';
import {
  initializeDataSourcesCustom,
  initializeDataSourcesCustomDimensionsSuccess,
  initializeDataSourcesCustomMetricsSuccess,
  loadDataSourcesCustom,
  loadDataSourcesCustomSuccess
} from './dataSourcesCustom.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {
  loadDataSetMetrics,
  loadDataSetMetricsSuccess,
  updateDataSetMetric, updateDataSetMetricsSuccess,
  updateDataSetMetricSuccess
} from '../dataSetMetrics/dataSetMetrics.actions';
import {selectDataSetMetricsState} from '../dataSetMetrics/dataSetMetrics.selectors';
import {filter} from 'rxjs/operators';
import {DataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions';
import {DataSetMetricsState} from '../dataSetMetrics/dataSetMetrics';
import {MetricComponentObjects} from '../../classes/metric-component-object';
import {Serializer, SerializerResponse} from "../../interfaces/serializer";
import {DataSource} from "../../interfaces/data-source";
import {loadDataSourceDimensions} from "../dataSourceDimensions/dataSourceDimensions.actions";
import {loadDataSourcesMetrics} from "../dataSourcesMetrics/dataSourcesMetrics.actions";
import {loadGeneralDimensionsSuccess} from "../generalDimensions/generalDimensions.actions";
import {selectGeneralDimensionsState} from "../generalDimensions/generalDimensions.selectors";
import {GeneralDimensionsState} from "../generalDimensions/generalDimensions";

@Injectable()
export class DataSourcesCustomEffects {

  public load$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesCustom.loadDataSourcesCustom),
    switchMap(() => forkJoin(
      [
        this._dataSourcesS.getCustomDataSetUsages(),
        this._dataSourcesS.getCustomImports()
      ])
      .pipe(
        map(([dataSources, imports]) => dataSourcesCustom.loadDataSourcesCustomSuccess({
          dataSources: dataSources.data,
          imports: imports.data,
          importsIncluded: imports.included,
        }))
      )
    )
  ));

  public updateCustomDataSource$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesCustom.updateCustomDataSource),
    exhaustMap((action) => this._dataSourcesS.updateDataSource(action.dataSource.id, action.update).pipe(
      map((result: SerializerResponse<Serializer<DataSource>>) => dataSourcesCustom.updateCustomDataSourceSuccess({
        dataSource: result.data
      }))
    ))
  ));

  public updateImport$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesCustom.updateCustomImport),
    exhaustMap((action) => this._dataSourcesS.updateCustomImportConfiguration(action.customImport, action.update).pipe(
      map((customImport) => dataSourcesCustom.updateCustomImportSuccess({
        customImport: customImport.data,
      }))
    ))
  ));

  public deleteImport$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesCustom.deleteCustomImport),
    exhaustMap((action) => this._dataSourcesS.deleteCustomImport(action.customImport.id).pipe(
      map(() => dataSourcesCustom.deleteCustomImportSuccess({
        customImport: action.customImport
      }))
    ))
  ));

  // ########## INIT ##########

  private readonly _init$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadDataSourcesCustom,
      loadDataSetDimensions,
      updateDataSetDimension,
      loadDataSetMetrics,
      updateDataSetMetric,
      loadDataSourceDimensions,
      loadDataSourcesMetrics
    ),
    map((): TypedAction<string> => initializeDataSourcesCustom())
  ));

  private readonly _initDimensionsSuccess$: Observable<TypedAction<string> & { dimensions: DimensionComponentObjects }> = createEffect((): Observable<TypedAction<string> & { dimensions: DimensionComponentObjects }> => this._actions$.pipe(
    ofType(
      loadDataSourcesCustomSuccess,
      loadDataSetDimensionsSuccess,
      loadGeneralDimensionsSuccess,
      updateDataSetDimensionSuccess,
      updateDataSetDimensionsSuccess
    ),
    withLatestFrom(this._store.select(selectDataSetDimensionsState), this._store.select(selectGeneralDimensionsState)),
    filter(([_, dataSetDimensionsState, generalDimensionsState]: [TypedAction<string>, DataSetDimensionsState, GeneralDimensionsState]): boolean => dataSetDimensionsState.loaded && generalDimensionsState.loaded),
    map(([_, dimensions, generalDimensions]: [TypedAction<string>, DataSetDimensionsState, GeneralDimensionsState]): TypedAction<string> & { dimensions: DimensionComponentObjects } => initializeDataSourcesCustomDimensionsSuccess({
      dimensions: dimensions.componentObjects.source,
      generalDimensions: generalDimensions.componentObjects
    }))
  ));

  private readonly _initMetricsSuccess$: Observable<TypedAction<string> & { metrics: MetricComponentObjects }> = createEffect((): Observable<TypedAction<string> & { metrics: MetricComponentObjects }> => this._actions$.pipe(
    ofType(
      loadDataSourcesCustomSuccess,
      loadDataSetMetricsSuccess,
      updateDataSetMetricSuccess,
      updateDataSetMetricsSuccess
    ),
    withLatestFrom(this._store.select(selectDataSetMetricsState)),
    filter(([_, dataSetMetricsState]: [TypedAction<string>, DataSetMetricsState]): boolean => dataSetMetricsState.loaded),
    map(([_, metrics]: [TypedAction<string>, DataSetMetricsState]): TypedAction<string> & { metrics: MetricComponentObjects } => initializeDataSourcesCustomMetricsSuccess({
      metrics: metrics.componentObjects.source
    }))
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _dataSourcesS: DataSetUsagesService,
    private readonly _store: Store<AppState>
  ) {}
}
